/**
 * Live business-config store.
 *
 * Components call `useBusinessConfig()` to get a reactive view of business
 * details (NAP, hours, social, CTAs). The store seeds from the static
 * `defaultBusiness` so SSR/first paint is stable, then a one-shot client
 * hydrator (`BusinessConfigHydrator`) fetches the canonical row from
 * `business_entity` and updates the store. Updates re-render all subscribed
 * components instantly — admin edits in `BusinessEntityManager` reach the
 * public site without a deploy.
 *
 * Non-React callers (tracking, schema generation, server fns) continue to
 * use `getBusinessConfig()` from `site-config.ts`, which we mirror to the
 * same store so they pick up DB values after hydration too.
 */

import { useEffect, useSyncExternalStore } from "react";
import { defaultBusiness, type BusinessConfig } from "./site-config";
import { getBusinessEntity } from "./business-entity.functions";
import type { Database } from "@/integrations/supabase/types";

type Entity = Omit<Database["public"]["Tables"]["business_entity"]["Row"], "enquiry_email">;

// ---------------------------------------------------------------------------
// DB row → BusinessConfig mapper
// ---------------------------------------------------------------------------

/** Format an E.164-ish UK phone to "020 7537 2447" style for display. */
function formatPhoneDisplay(raw: string): string {
  const digits = raw.replace(/[^\d]/g, "");
  if (digits.startsWith("44") && digits.length === 12) {
    // 442075372447 → 020 7537 2447
    return `0${digits.slice(2, 4)} ${digits.slice(4, 8)} ${digits.slice(8, 12)}`;
  }
  return raw;
}

/** Format an E.164-ish UK phone to "tel:+44..." href. */
function formatPhoneHref(raw: string): string {
  const digits = raw.replace(/[^\d]/g, "");
  if (digits.startsWith("0") && digits.length === 11) return `tel:+44${digits.slice(1)}`;
  if (digits.startsWith("44")) return `tel:+${digits}`;
  return `tel:${raw}`;
}

/** Map a `business_entity` row to the `BusinessConfig` shape. Falls back to
 *  default values for any missing/null DB fields so consumers never crash. */
export function mapEntityToConfig(row: Entity | null | undefined): BusinessConfig {
  if (!row) return defaultBusiness;

  const phoneDigits = row.phone.replace(/[^\d+]/g, "");
  const hoursRaw = Array.isArray(row.opening_hours) ? (row.opening_hours as unknown[]) : [];
  const hours = hoursRaw
    .filter((h): h is { day: string; time: string } =>
      !!h && typeof h === "object" && "day" in h && "time" in h,
    )
    .map((h) => ({ day: String(h.day), time: String(h.time) }));

  return {
    name: row.trading_name || defaultBusiness.name,
    phone: formatPhoneDisplay(row.phone),
    phoneHref: formatPhoneHref(row.phone),
    email: row.public_email || defaultBusiness.email,
    address: row.registered_street || defaultBusiness.address,
    addressLine2: [row.registered_locality, row.registered_region].filter(Boolean).join(", ") || defaultBusiness.addressLine2,
    entrance: row.arrival_explanation || defaultBusiness.entrance,
    postcode: row.registered_postcode || defaultBusiness.postcode,
    vtsNumber: row.mot_station_number || defaultBusiness.vtsNumber,
    instagram: defaultBusiness.instagram,
    instagramUrl: row.instagram_url || defaultBusiness.instagramUrl,
    linktreeUrl: row.linktree_url || defaultBusiness.linktreeUrl,
    googleReviewsUrl: row.google_review_url || defaultBusiness.googleReviewsUrl,
    qrImagePath: defaultBusiness.qrImagePath,
    googleMapsUrl: row.arrival_directions_url || defaultBusiness.googleMapsUrl,
    googleMapsEmbedUrl: row.arrival_map_embed_url || defaultBusiness.googleMapsEmbedUrl,
    whatsappUrl: defaultBusiness.whatsappUrl.replace(/wa\.me\/\d+/, () => {
      const d = phoneDigits.replace(/^\+/, "");
      return `wa.me/${d || "442075372447"}`;
    }),
    hours: hours.length ? hours : defaultBusiness.hours,
    launchChecklist: defaultBusiness.launchChecklist,
  };
}

// ---------------------------------------------------------------------------
// External store + reactive hook
// ---------------------------------------------------------------------------

let current: BusinessConfig = defaultBusiness;
const listeners = new Set<() => void>();

/** Internal setter — call from the hydrator only. */
function setCurrent(next: BusinessConfig) {
  if (next === current) return;
  current = next;
  listeners.forEach((l) => l());
}

/** Read the live config synchronously. Used by non-React callers. */
export function readBusinessConfig(): BusinessConfig {
  return current;
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot() {
  return current;
}

function getServerSnapshot() {
  // SSR always renders the static defaults to guarantee a stable first paint.
  return defaultBusiness;
}

/** Reactive hook — subscribed components re-render when the store updates. */
export function useBusinessConfig(): BusinessConfig {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

// ---------------------------------------------------------------------------
// Hydrator
// ---------------------------------------------------------------------------

let hydrationPromise: Promise<void> | null = null;

/** Mount once near the root. Fetches the canonical business entity from the
 *  DB on first client render and pushes it into the store. Idempotent. */
export function BusinessConfigHydrator() {
  useEffect(() => {
    if (hydrationPromise) return;
    hydrationPromise = (async () => {
      try {
        const row = await getBusinessEntity();
        if (row) setCurrent(mapEntityToConfig(row));
      } catch {
        // DB unreachable → keep defaults. No user-visible error.
      }
    })();
  }, []);
  return null;
}
